import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import {
  Sample,
  Client,
  Supplier,
  Product,
  SampleType,
  SampleStatus,
  samplesAPI,
  clientsAPI,
  suppliersAPI,
  productsAPI
} from '../../services/api';
import {
  ArrowLeftIcon,
  BeakerIcon,
  BuildingOfficeIcon,
  TruckIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import { Card, CardBody, Button, Spinner } from '../ui';

const SampleForm: React.FC = () => {
  const navigate = useNavigate();
  const { clientId, id } = useParams();
  const location = useLocation();
  const isEdit = Boolean(id);

  // Check if we're in admin context
  const isAdminContext = location.pathname.startsWith('/admin');
  const getSamplePath = (path: string) => {
    return isAdminContext ? `/admin${path}` : path;
  };

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  // Form data
  const [formData, setFormData] = useState({
    // Core Tracking
    trackingNumber: '',
    lotNumber: '',
    sampleCode: '',
    
    // Client & Relationships
    clientId: clientId || '',
    supplierId: '',
    supplierOfferId: '',
    
    // Product Information
    productId: '',
    productName: '',
    productSku: '',
    quantity: '',
    unit: 'piece',
    
    // Sample Details
    sampleType: 'FREE_SAMPLE' as SampleType,
    status: 'REQUESTED' as SampleStatus,
    batchNumber: '',
    manufacturingDate: '',
    expiryDate: '',
    
    // Cost Tracking
    sampleCost: '',
    shippingCost: '',
    totalCost: '',
    currency: 'USD',
    
    // Shipping Information
    shippingCarrier: '',
    shippingMethod: '',
    shippingDate: '',
    estimatedDeliveryDate: '',
    
    // Documentation
    notes: '',
    specialInstructions: '',
    
    // Metadata
    assignedUserId: '',
  });

  // Dropdowns and search
  const [clients, setClients] = useState<Client[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);

  const [clientSearch, setClientSearch] = useState('');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [productSearch, setProductSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showSupplierDropdown, setShowSupplierDropdown] = useState(false);
  const [showProductDropdown, setShowProductDropdown] = useState(false);

  useEffect(() => {
    fetchClients();
    fetchSuppliers();
    fetchProducts();
    
    if (isEdit && id) {
      fetchSample();
    } else if (clientId) {
      // Pre-select client if coming from client detail page
      fetchClientById(Number(clientId));
    }
  }, [id, clientId]);

  const fetchClients = async () => {
    try {
      const response = await clientsAPI.getAll({});
      setClients(response.data || []);
    } catch (err) {
      console.error('Failed to fetch clients:', err);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await suppliersAPI.getAll({});
      setSuppliers(response.data || []);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
    }
  };

  const fetchProducts = async () => {
    try {
      const products = await productsAPI.getAll({});
      setProducts(products || []);
    } catch (err) {
      console.error('Failed to fetch products:', err);
    }
  };

  const fetchClientById = async (id: number) => {
    try {
      const client = await clientsAPI.getById(id);
      setSelectedClient(client);
      setClientSearch(client.name);
      setFormData(prev => ({ ...prev, clientId: client.id.toString() }));
    } catch (err) {
      console.error('Failed to fetch client:', err);
    }
  };

  const fetchSample = async () => {
    try {
      setLoading(true);
      const sample = await samplesAPI.getById(Number(id));

      setFormData({
        trackingNumber: sample.trackingNumber || '',
        lotNumber: sample.lotNumber || '',
        sampleCode: sample.sampleCode || '',
        clientId: sample.clientId.toString(),
        supplierId: sample.supplierId?.toString() || '',
        supplierOfferId: sample.supplierOfferId?.toString() || '',
        productId: sample.productId?.toString() || '',
        productName: sample.productName,
        productSku: sample.productSku || '',
        quantity: sample.quantity,
        unit: sample.unit,
        sampleType: sample.sampleType,
        status: sample.status,
        batchNumber: sample.batchNumber || '',
        manufacturingDate: sample.manufacturingDate || '',
        expiryDate: sample.expiryDate || '',
        sampleCost: sample.sampleCost || '',
        shippingCost: sample.shippingCost || '',
        totalCost: sample.totalCost || '',
        currency: sample.currency,
        shippingCarrier: sample.shippingCarrier || '',
        shippingMethod: sample.shippingMethod || '',
        shippingDate: sample.shippingDate || '',
        estimatedDeliveryDate: sample.estimatedDeliveryDate || '',
        notes: sample.notes || '',
        specialInstructions: sample.specialInstructions || '',
        assignedUserId: sample.assignedUserId?.toString() || '',
      });

      if (sample.client) {
        setSelectedClient(sample.client as any);
        setClientSearch(sample.client.name);
      }
      if (sample.supplier) {
        setSelectedSupplier(sample.supplier as any);
        setSupplierSearch(sample.supplier.name);
      }
      if (sample.product) {
        setSelectedProduct(sample.product as any);
        setProductSearch(sample.product.name);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch sample');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Auto-calculate total cost
    if (name === 'sampleCost' || name === 'shippingCost') {
      const sampleCost = name === 'sampleCost' ? parseFloat(value) || 0 : parseFloat(formData.sampleCost) || 0;
      const shippingCost = name === 'shippingCost' ? parseFloat(value) || 0 : parseFloat(formData.shippingCost) || 0;
      setFormData(prev => ({ ...prev, totalCost: (sampleCost + shippingCost).toString() }));
    }
  };

  const handleClientSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setClientSearch(e.target.value);
    setShowClientDropdown(true);
  };

  const handleSupplierSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSupplierSearch(e.target.value);
    setShowSupplierDropdown(true);
  };

  const handleProductSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProductSearch(e.target.value);
    setShowProductDropdown(true);
  };

  const handleClientSelect = (client: Client) => {
    setSelectedClient(client);
    setFormData(prev => ({ ...prev, clientId: client.id.toString() }));
    setClientSearch(client.name);
    setShowClientDropdown(false);
    setFieldErrors(prev => ({ ...prev, clientId: '' }));
  };

  const handleSupplierSelect = (supplier: Supplier) => {
    setSelectedSupplier(supplier);
    setFormData(prev => ({ ...prev, supplierId: supplier.id.toString() }));
    setSupplierSearch(supplier.name);
    setShowSupplierDropdown(false);
  };

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
    setFormData(prev => ({
      ...prev,
      productId: product.id.toString(),
      productName: product.name,
      productSku: product.sku,
      unit: product.unit || 'piece'
    }));
    setProductSearch(product.name);
    setShowProductDropdown(false);
    setFieldErrors(prev => ({ ...prev, productName: '' }));
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase())
  );

  const filteredSuppliers = suppliers.filter(supplier =>
    supplier.name.toLowerCase().includes(supplierSearch.toLowerCase())
  );

  const filteredProducts = products.filter(product =>
    product.name.toLowerCase().includes(productSearch.toLowerCase()) ||
    product.sku.toLowerCase().includes(productSearch.toLowerCase())
  );

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.clientId) {
      errors.clientId = 'Please select a client';
    }
    if (!formData.productName.trim()) {
      errors.productName = 'Product name is required';
    }
    if (!formData.quantity || parseFloat(formData.quantity) <= 0) {
      errors.quantity = 'Quantity must be greater than 0';
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      const submitData: any = {
        trackingNumber: formData.trackingNumber || undefined,
        lotNumber: formData.lotNumber || undefined,
        sampleCode: formData.sampleCode || undefined,
        clientId: Number(formData.clientId),
        supplierId: formData.supplierId ? Number(formData.supplierId) : undefined,
        supplierOfferId: formData.supplierOfferId ? Number(formData.supplierOfferId) : undefined,
        productId: formData.productId ? Number(formData.productId) : undefined,
        productName: formData.productName,
        productSku: formData.productSku || undefined,
        quantity: parseFloat(formData.quantity),
        unit: formData.unit,
        sampleType: formData.sampleType,
        status: isEdit ? formData.status : 'REQUESTED',
        batchNumber: formData.batchNumber || undefined,
        manufacturingDate: formData.manufacturingDate || undefined,
        expiryDate: formData.expiryDate || undefined,
        sampleCost: formData.sampleCost ? parseFloat(formData.sampleCost) : undefined,
        shippingCost: formData.shippingCost ? parseFloat(formData.shippingCost) : undefined,
        totalCost: formData.totalCost ? parseFloat(formData.totalCost) : undefined,
        currency: formData.currency,
        shippingCarrier: formData.shippingCarrier || undefined,
        shippingMethod: formData.shippingMethod || undefined,
        shippingDate: formData.shippingDate || undefined,
        estimatedDeliveryDate: formData.estimatedDeliveryDate || undefined,
        notes: formData.notes || undefined,
        specialInstructions: formData.specialInstructions || undefined,
        assignedUserId: formData.assignedUserId ? Number(formData.assignedUserId) : undefined,
      };

      if (isEdit && id) {
        await samplesAPI.update(Number(id), submitData);
        setSuccess('Sample updated successfully!');
      } else {
        await samplesAPI.create(submitData);
        setSuccess('Sample created successfully!');
      }

      setTimeout(() => {
        if (clientId) {
          navigate(getSamplePath(`/clients/${clientId}`));
        } else {
          navigate(getSamplePath('/samples'));
        }
      }, 1500);
    } catch (err: any) {
      setError(err.response?.data?.message || `Failed to ${isEdit ? 'update' : 'create'} sample`);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="lg" />
          <p className="mt-4 text-neutral-600">Loading sample...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-8">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => clientId ? navigate(getSamplePath(`/clients/${clientId}`)) : navigate(getSamplePath('/samples'))}
            className="flex items-center text-sm text-neutral-500 hover:text-neutral-700 mb-4"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Back to {clientId ? 'Client' : 'Samples'}
          </button>
          <div className="flex items-center">
            <BeakerIcon className="h-8 w-8 text-primary-600 mr-3" />
            <div>
              <h1 className="text-3xl font-bold text-neutral-900">
                {isEdit ? 'Edit Sample' : 'Request New Sample'}
              </h1>
              <p className="mt-1 text-sm text-neutral-500">
                {isEdit ? 'Update sample information' : 'Start a sample request before shipping details are available'}
              </p>
            </div>
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="mb-6 bg-error-50 border border-error-200 rounded-lg p-4 flex items-start">
            <ExclamationTriangleIcon className="h-5 w-5 text-error-400 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-error-800">Error</h3>
              <p className="mt-1 text-sm text-error-700">{error}</p>
            </div>
          </div>
        )}

        {success && (
          <div className="mb-6 bg-success-50 border border-success-200 rounded-lg p-4 flex items-start">
            <CheckCircleIcon className="h-5 w-5 text-success-400 mr-3 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-success-800">Success</h3>
              <p className="mt-1 text-sm text-success-700">{success}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Client & Product Information Card */}
          <Card variant="elevated">
            <CardBody className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <BuildingOfficeIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Client & Product Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Select the client and product for this sample
                  </p>
                </div>

                {/* Client Selection */}
                <div className="sm:col-span-6">
                  <label htmlFor="clientSearch" className="block text-sm font-medium leading-6 text-neutral-900">
                    Client *
                  </label>
                  <div className="mt-2 relative">
                    <input
                      type="text"
                      id="clientSearch"
                      autoComplete="off"
                      required
                      disabled={!!clientId}
                      className={`block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 transition-colors ${
                        fieldErrors.clientId
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      } ${clientId ? 'bg-neutral-100' : ''}`}
                      value={clientSearch}
                      onChange={handleClientSearchChange}
                      onFocus={() => !clientId && setShowClientDropdown(true)}
                      placeholder="Search for a client..."
                    />
                    {showClientDropdown && !clientId && filteredClients.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredClients.map((client) => (
                          <div
                            key={client.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                            onClick={() => handleClientSelect(client)}
                          >
                            <div className="flex items-center">
                              <span className="font-medium block truncate">{client.name}</span>
                            </div>
                            {client.email && (
                              <span className="text-neutral-500 text-xs block truncate">{client.email}</span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {fieldErrors.clientId && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.clientId}
                      </p>
                    )}
                  </div>
                </div>

                {/* Product Search */}
                <div className="sm:col-span-4">
                  <label htmlFor="productSearch" className="block text-sm font-medium leading-6 text-neutral-900">
                    Product (Optional - can enter manually)
                  </label>
                  <div className="mt-2 relative">
                    <input
                      type="text"
                      id="productSearch"
                      autoComplete="off"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={productSearch}
                      onChange={handleProductSearchChange}
                      onFocus={() => setShowProductDropdown(true)}
                      placeholder="Search for a product..."
                    />
                    {showProductDropdown && filteredProducts.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredProducts.map((product) => (
                          <div
                            key={product.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                            onClick={() => handleProductSelect(product)}
                          >
                            <div className="flex items-center justify-between">
                              <span className="font-medium block truncate">{product.name}</span>
                              <span className="text-neutral-500 text-xs">{product.sku}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Product Name */}
                <div className="sm:col-span-4">
                  <label htmlFor="productName" className="block text-sm font-medium leading-6 text-neutral-900">
                    Product Name *
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="productName"
                      id="productName"
                      required
                      className={`block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                        fieldErrors.productName
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      }`}
                      value={formData.productName}
                      onChange={handleChange}
                      placeholder="Enter product name"
                    />
                    {fieldErrors.productName && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.productName}
                      </p>
                    )}
                  </div>
                </div>

                {/* Product SKU */}
                <div className="sm:col-span-2">
                  <label htmlFor="productSku" className="block text-sm font-medium leading-6 text-neutral-900">
                    SKU
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="productSku"
                      id="productSku"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.productSku}
                      onChange={handleChange}
                      placeholder="SKU"
                    />
                  </div>
                </div>

                {/* Supplier Search */}
                <div className="sm:col-span-6">
                  <label htmlFor="supplierSearch" className="block text-sm font-medium leading-6 text-neutral-900">
                    Supplier (Optional)
                  </label>
                  <div className="mt-2 relative">
                    <input
                      type="text"
                      id="supplierSearch"
                      autoComplete="off"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={supplierSearch}
                      onChange={handleSupplierSearchChange}
                      onFocus={() => setShowSupplierDropdown(true)}
                      placeholder="Search for a supplier..."
                    />
                    {showSupplierDropdown && filteredSuppliers.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white shadow-lg max-h-60 rounded-md py-1 text-base ring-1 ring-black ring-opacity-5 overflow-auto focus:outline-none sm:text-sm">
                        {filteredSuppliers.map((supplier) => (
                          <div
                            key={supplier.id}
                            className="cursor-pointer select-none relative py-2 pl-3 pr-9 hover:bg-primary-50"
                            onClick={() => handleSupplierSelect(supplier)}
                          >
                            <span className="font-medium block truncate">{supplier.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Tracking & Sample Details Card */}
          <Card variant="elevated">
            <CardBody className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <BeakerIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Tracking & Sample Details
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Add request details now; lot and tracking can be added when the sample ships
                  </p>
                </div>

                {/* Tracking Number */}
                <div className="sm:col-span-3">
                  <label htmlFor="trackingNumber" className="block text-sm font-medium leading-6 text-neutral-900">
                    Tracking Number
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="trackingNumber"
                      id="trackingNumber"
                      className={`block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                        fieldErrors.trackingNumber
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      }`}
                      value={formData.trackingNumber}
                      onChange={handleChange}
                      placeholder="e.g., TRK-2024-001"
                    />
                    {fieldErrors.trackingNumber && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.trackingNumber}
                      </p>
                    )}
                  </div>
                </div>

                {/* Lot Number */}
                <div className="sm:col-span-3">
                  <label htmlFor="lotNumber" className="block text-sm font-medium leading-6 text-neutral-900">
                    Lot Number
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="lotNumber"
                      id="lotNumber"
                      className={`block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                        fieldErrors.lotNumber
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      }`}
                      value={formData.lotNumber}
                      onChange={handleChange}
                      placeholder="e.g., LOT-2024-A123"
                    />
                    {fieldErrors.lotNumber && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.lotNumber}
                      </p>
                    )}
                  </div>
                </div>

                {/* Sample Code */}
                <div className="sm:col-span-2">
                  <label htmlFor="sampleCode" className="block text-sm font-medium leading-6 text-neutral-900">
                    Sample Code
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="sampleCode"
                      id="sampleCode"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.sampleCode}
                      onChange={handleChange}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {/* Batch Number */}
                <div className="sm:col-span-2">
                  <label htmlFor="batchNumber" className="block text-sm font-medium leading-6 text-neutral-900">
                    Batch Number
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="batchNumber"
                      id="batchNumber"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.batchNumber}
                      onChange={handleChange}
                      placeholder="Optional"
                    />
                  </div>
                </div>

                {/* Sample Type */}
                <div className="sm:col-span-2">
                  <label htmlFor="sampleType" className="block text-sm font-medium leading-6 text-neutral-900">
                    Sample Type
                  </label>
                  <div className="mt-2">
                    <select
                      name="sampleType"
                      id="sampleType"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.sampleType}
                      onChange={handleChange}
                    >
                      <option value="FREE_SAMPLE">Free Sample</option>
                      <option value="PAID_SAMPLE">Paid Sample</option>
                      <option value="TRIAL">Trial</option>
                      <option value="DEMO">Demo</option>
                      <option value="REPLACEMENT">Replacement</option>
                      <option value="QUALITY_CHECK">Quality Check</option>
                    </select>
                  </div>
                </div>

                {/* Quantity */}
                <div className="sm:col-span-2">
                  <label htmlFor="quantity" className="block text-sm font-medium leading-6 text-neutral-900">
                    Quantity *
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="quantity"
                      id="quantity"
                      required
                      step="0.01"
                      min="0"
                      className={`block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6 ${
                        fieldErrors.quantity
                          ? 'ring-error-300 focus:ring-error-500'
                          : 'ring-neutral-300 focus:ring-primary-600'
                      }`}
                      value={formData.quantity}
                      onChange={handleChange}
                      placeholder="0"
                    />
                    {fieldErrors.quantity && (
                      <p className="mt-2 text-sm text-error-600 flex items-center">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        {fieldErrors.quantity}
                      </p>
                    )}
                  </div>
                </div>

                {/* Unit */}
                <div className="sm:col-span-2">
                  <label htmlFor="unit" className="block text-sm font-medium leading-6 text-neutral-900">
                    Unit
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="unit"
                      id="unit"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.unit}
                      onChange={handleChange}
                      placeholder="piece, kg, liter, etc."
                    />
                  </div>
                </div>

                {/* Status */}
                <div className="sm:col-span-2">
                  <label htmlFor="status" className="block text-sm font-medium leading-6 text-neutral-900">
                    Status
                  </label>
                  <div className="mt-2">
                    <select
                      name="status"
                      id="status"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.status}
                      onChange={handleChange}
                    >
                      <option value="REQUESTED">Requested</option>
                      <option value="PENDING">Pending</option>
                      <option value="SHIPPED">Shipped</option>
                      <option value="DELIVERED">Delivered</option>
                      <option value="UNDER_REVIEW">Under Review</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="RETURNED">Returned</option>
                      <option value="LOST">Lost</option>
                      <option value="EXPIRED">Expired</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Shipping Information Card */}
          <Card variant="elevated">
            <CardBody className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <TruckIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Shipping Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Enter shipping and delivery details
                  </p>
                </div>

                {/* Shipping Carrier */}
                <div className="sm:col-span-3">
                  <label htmlFor="shippingCarrier" className="block text-sm font-medium leading-6 text-neutral-900">
                    Shipping Carrier
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="shippingCarrier"
                      id="shippingCarrier"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.shippingCarrier}
                      onChange={handleChange}
                      placeholder="e.g., FedEx, DHL, UPS"
                    />
                  </div>
                </div>

                {/* Shipping Method */}
                <div className="sm:col-span-3">
                  <label htmlFor="shippingMethod" className="block text-sm font-medium leading-6 text-neutral-900">
                    Shipping Method
                  </label>
                  <div className="mt-2">
                    <input
                      type="text"
                      name="shippingMethod"
                      id="shippingMethod"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.shippingMethod}
                      onChange={handleChange}
                      placeholder="e.g., Express, Standard"
                    />
                  </div>
                </div>

                {/* Shipping Date */}
                <div className="sm:col-span-3">
                  <label htmlFor="shippingDate" className="block text-sm font-medium leading-6 text-neutral-900">
                    Shipping Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="shippingDate"
                      id="shippingDate"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.shippingDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                {/* Estimated Delivery Date */}
                <div className="sm:col-span-3">
                  <label htmlFor="estimatedDeliveryDate" className="block text-sm font-medium leading-6 text-neutral-900">
                    Estimated Delivery Date
                  </label>
                  <div className="mt-2">
                    <input
                      type="date"
                      name="estimatedDeliveryDate"
                      id="estimatedDeliveryDate"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.estimatedDeliveryDate}
                      onChange={handleChange}
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Cost Information Card */}
          <Card variant="elevated">
            <CardBody className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <CurrencyDollarIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Cost Information
                  </h3>
                  <p className="mt-1 text-sm leading-6 text-neutral-600">
                    Enter cost details (optional)
                  </p>
                </div>

                {/* Sample Cost */}
                <div className="sm:col-span-2">
                  <label htmlFor="sampleCost" className="block text-sm font-medium leading-6 text-neutral-900">
                    Sample Cost
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="sampleCost"
                      id="sampleCost"
                      step="0.01"
                      min="0"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.sampleCost}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Shipping Cost */}
                <div className="sm:col-span-2">
                  <label htmlFor="shippingCost" className="block text-sm font-medium leading-6 text-neutral-900">
                    Shipping Cost
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="shippingCost"
                      id="shippingCost"
                      step="0.01"
                      min="0"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.shippingCost}
                      onChange={handleChange}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Total Cost (Auto-calculated) */}
                <div className="sm:col-span-2">
                  <label htmlFor="totalCost" className="block text-sm font-medium leading-6 text-neutral-900">
                    Total Cost
                  </label>
                  <div className="mt-2">
                    <input
                      type="number"
                      name="totalCost"
                      id="totalCost"
                      step="0.01"
                      min="0"
                      disabled
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 bg-neutral-50 shadow-sm ring-1 ring-inset ring-neutral-300 sm:text-sm sm:leading-6"
                      value={formData.totalCost}
                      placeholder="0.00"
                    />
                  </div>
                </div>

                {/* Currency */}
                <div className="sm:col-span-2">
                  <label htmlFor="currency" className="block text-sm font-medium leading-6 text-neutral-900">
                    Currency
                  </label>
                  <div className="mt-2">
                    <select
                      name="currency"
                      id="currency"
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.currency}
                      onChange={handleChange}
                    >
                      <option value="USD">USD</option>
                      <option value="EUR">EUR</option>
                      <option value="GBP">GBP</option>
                      <option value="JPY">JPY</option>
                      <option value="CNY">CNY</option>
                    </select>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Additional Information Card */}
          <Card variant="elevated">
            <CardBody className="px-4 py-6 sm:p-8">
              <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6">
                <div className="sm:col-span-6">
                  <h3 className="text-lg font-semibold leading-7 text-neutral-900 flex items-center">
                    <DocumentTextIcon className="h-5 w-5 mr-2 text-neutral-400" />
                    Additional Information
                  </h3>
                </div>

                {/* Notes */}
                <div className="sm:col-span-6">
                  <label htmlFor="notes" className="block text-sm font-medium leading-6 text-neutral-900">
                    Notes
                  </label>
                  <div className="mt-2">
                    <textarea
                      name="notes"
                      id="notes"
                      rows={3}
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.notes}
                      onChange={handleChange}
                      placeholder="Any additional notes about this sample..."
                    />
                  </div>
                </div>

                {/* Special Instructions */}
                <div className="sm:col-span-6">
                  <label htmlFor="specialInstructions" className="block text-sm font-medium leading-6 text-neutral-900">
                    Special Instructions
                  </label>
                  <div className="mt-2">
                    <textarea
                      name="specialInstructions"
                      id="specialInstructions"
                      rows={2}
                      className="block w-full rounded-md border-0 py-2.5 px-3 text-neutral-900 shadow-sm ring-1 ring-inset ring-neutral-300 focus:ring-2 focus:ring-inset focus:ring-primary-600 sm:text-sm sm:leading-6"
                      value={formData.specialInstructions}
                      onChange={handleChange}
                      placeholder="Special handling or delivery instructions..."
                    />
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-x-6 pb-8">
            <Button
              type="button"
              variant="ghost"
              onClick={() => clientId ? navigate(getSamplePath(`/clients/${clientId}`)) : navigate(getSamplePath('/samples'))}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              disabled={saving}
              isLoading={saving}
            >
              {isEdit ? 'Update Sample' : 'Create Sample'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SampleForm;

